
import React, { useState, useEffect } from 'react';
import './LayerBox.css';

export default function NewLayerBox({ viewerRef, name, layers, layerVisibility, toggleLayerVisibility, currentIndex, stopAnimation }) {
    // Find the currently visible timestamp (first true in layerVisibility)
    const timestamps = layers.map(layer => layer.ts);

    // Include "No Flooding" option at index 0 (null), then timestamps at indices 1-9
    const sliderOptions = [null, ...timestamps];

    const [selectedIndex, setSelectedIndex] = useState(() => {
        const visibleTs = timestamps?.find(ts => layerVisibility[ts]);
        if (visibleTs !== undefined) {
            return timestamps.indexOf(visibleTs) + 1; // +1 to account for "No Flooding" at index 0
        }
        return 0; // Default to "No Flooding"
    });

    // Sync slider position with layerVisibility changes (e.g., from auto-animation)
    // But only if currentIndex prop is not being used to control the slider
    useEffect(() => {
        // If currentIndex prop exists (even if null), it takes precedence over layerVisibility
        // undefined means the prop is not being used, null/number means it's explicitly controlling
        if (currentIndex !== undefined) {
            console.log(`[NewLayerBox ${name}] Skipping layerVisibility sync, currentIndex prop is controlling slider:`, currentIndex);
            return;
        }

        const visibleTs = timestamps?.find(ts => layerVisibility[ts]);
        console.log(`[NewLayerBox ${name}] layerVisibility changed, visibleTs:`, visibleTs);
        if (visibleTs !== undefined) {
            const newIndex = timestamps.indexOf(visibleTs) + 1;
            console.log(`[NewLayerBox ${name}] Setting slider from layerVisibility to:`, newIndex);
            setSelectedIndex(newIndex);
        } else {
            // No timestamp is visible, set to "No Flooding"
            console.log(`[NewLayerBox ${name}] No visible layer, setting slider to 0`);
            setSelectedIndex(0);
        }
    }, [layerVisibility, timestamps, name, currentIndex]);

    // Sync slider position with currentIndex prop (e.g., from walkthrough setTimestampIndex action)
    useEffect(() => {
        console.log(`[NewLayerBox ${name}] currentIndex prop changed to:`, currentIndex);
        if (currentIndex === null) {
            // null means "No Flooding" - set slider to position 0
            console.log(`[NewLayerBox ${name}] currentIndex is null, setting slider to 0`);
            setSelectedIndex(0);
        } else if (currentIndex !== undefined) {
            // currentIndex is 0-based array index, slider needs +1 for "No Flooding" offset
            const sliderIndex = currentIndex + 1;
            console.log(`[NewLayerBox ${name}] Setting slider to:`, sliderIndex);
            setSelectedIndex(sliderIndex);
        }
    }, [currentIndex, name]);

    const handleSliderChange = async (e) => {
        // Stop any ongoing animation when user manually interacts with slider
        if (stopAnimation && typeof stopAnimation === 'function') {
            stopAnimation();
        }

        const sliderValue = parseInt(e.target.value, 10);
        setSelectedIndex(sliderValue);

        if (sliderValue === 0) {
            // "No Flooding" selected - hide all timestamps
            const toHide = timestamps.filter(t => layerVisibility[t]);
            for (const ts of toHide) {
                const layer = layers?.find(l => l.ts === ts);
                if (layer) {
                    await toggleLayerVisibility(ts, layer.name);
                }
            }
        } else {
            // A timestamp is selected
            const ts = timestamps[sliderValue - 1]; // -1 to account for "No Flooding" offset

            // Hide all other timestamps
            const toHide = timestamps.filter(t => t !== ts && layerVisibility[t]);
            for (const otherTs of toHide) {
                const otherLayer = layers?.find(l => l.ts === otherTs);
                if (otherLayer) {
                    await toggleLayerVisibility(otherTs, otherLayer.name);
                }
            }

            // Show the selected timestamp if not visible
            if (!layerVisibility[ts]) {
                const layer = layers?.find(l => l.ts === ts);
                if (layer) {
                    await toggleLayerVisibility(ts, layer.name);
                }
            }
        }
    };

    return (
        <div className="tables-container">
            <div className="layer-control-header">
                <div className="" style={{ position: 'relative', background: 'transparent' }}>
                    <input
                        type="range"
                        min={0}
                        max={sliderOptions.length - 1}
                        value={selectedIndex}
                        onChange={handleSliderChange}
                        step={1}
                        style={{ width: '100%' }}
                    />
                        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'absolute', width: '100%', top: 30 }}>
                            {sliderOptions.map((option, idx) => (
                                <div key={idx} style={{ textAlign: 'center', width: '1px', flex: 1 }}>
                                    <div style={{
                                        width: 6,
                                        height: 12,
                                        background: selectedIndex === idx ? '#1976d2' : '#ccc',
                                        margin: '0 auto',
                                        borderRadius: 1,
                                    }}></div>
                                </div>
                            ))}
                        </div>
                </div>
            </div>
        </div>
    );
}
